import { create } from 'zustand';
import type { Playlist, PlaylistInput, PlaylistItemInput } from '@scio3/shared-types';

export type PlaylistDialogType =
  | 'createPlaylist'
  | 'createFolder'
  | 'rename'
  | 'renameFolder'
  | 'options'
  | 'itemOptions'
  | 'editDuration'
  | 'whereUsed'
  | 'moveFolder'
  | 'duplicate'
  | 'push'
  | 'delete'
  | 'deleteFolder'
  | 'folderPermission'
  | 'addSubPlaylist'
  | 'confirmFolderDrop'
  | 'emptyFolderWarning'
  | 'preview'
  | 'feedEditor'
  | null;

/** Folder group shape with security fields for move dialog */
export interface PlaylistGroup {
  _id: string;
  name: string;
  type?: string;
  path?: string;
  teamId?: string;
  isFolderSecurityEdit?: boolean;
  isFolderSecurityView?: boolean;
  securities?: Array<{ userId: string; role: string }>;
  securityTeams?: string[];
  sharedLink?: boolean;
}

interface PlaylistDialogContext {
  playlist?: Playlist;
  /** Feed ID for opening FeedEditorDialog directly (e.g. from Feeds Admin page) */
  feedId?: string;
  playlistIds?: string[];
  groupIds?: string[];
  folder?: PlaylistGroup;
  itemIndices?: number[];
  isReadOnly?: boolean;
  /** Callback for debounced item-level updates (used by EditDurationDialog, ItemOptionsDialog).
   *  Updates local state optimistically and marks items dirty for debounced auto-save. */
  onItemsUpdate?: (updates: Array<{ index: number; fields: Partial<PlaylistItemInput> }>) => void;
  /** Callback for debounced playlist-level option updates (used by PlaylistOptionsDialog).
   *  Merges options payload into pending auto-save instead of calling API immediately. */
  onOptionsUpdate?: (payload: Partial<PlaylistInput>) => void;
  /** Context for folder-drop confirmation dialog. */
  folderDropContext?: {
    folderName: string;
    assetCount: number;
    onConfirm: () => void;
  };
}

interface PlaylistDialogState {
  activeDialog: PlaylistDialogType;
  dialogContext: PlaylistDialogContext;
  openDialog: (dialog: PlaylistDialogType, context?: PlaylistDialogContext) => void;
  closeDialog: () => void;
}

export const usePlaylistDialogStore = create<PlaylistDialogState>((set) => ({
  activeDialog: null,
  dialogContext: {},
  openDialog: (activeDialog, dialogContext = {}) => set({ activeDialog, dialogContext }),
  closeDialog: () => set({ activeDialog: null, dialogContext: {} }),
}));
